import React, { useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useChangeTenant } from '@area-restrita/api-client/endpoints/auth/auth.gen';

import { Form } from '@/components/ui/form';
import { useAuthSessionStore } from '../../stores/authSessionStore';
import { resolveOnboardingRedirect } from '../../utils/redirectFlow';

import { OnboardingSchema, onboardingSchema } from './schema';
import OnboardingView from './view';

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const { signIn, consumePendingRedirect } = useAuthSessionStore();
  const form = useForm<OnboardingSchema>({
    resolver: zodResolver(onboardingSchema),
  });

  const changeTenantMutation = useChangeTenant({
    mutation: {
      onSuccess: ({ accessToken }) => {
        signIn(accessToken);

        const nextRedirect = resolveOnboardingRedirect(
          consumePendingRedirect(),
        );

        navigate({ to: nextRedirect });
      },
    },
  });

  const handleSubmit = useCallback<SubmitHandler<OnboardingSchema>>(
    ({ organization }) =>
      changeTenantMutation.mutateAsync({
        data: {
          tenantSlug: organization.slug,
          companyId: organization.companyId,
        },
      }),
    [changeTenantMutation],
  );

  return (
    <Form {...form}>
      <OnboardingView
        control={form.control}
        onSubmit={form.handleSubmit(handleSubmit)}
        isLoading={form.formState.isSubmitting}
      />
    </Form>
  );
};

export default Onboarding;
